import { SpecialZoneType } from '@hakimo-ui/hakimo/ui-elements';
import { Checkbox, HakimoSpinner } from '@hakimo-ui/shared/ui-base';
import { Popover } from '@headlessui/react';
import clsx from 'clsx';
import { LandPlotIcon } from 'lucide-react';
import { useIsFetching } from 'react-query';
import { ZONE_OVERLAY_OPTIONS } from '../constants';
import { useAlarmGroupTracking } from '../useAlarmGroupTracking';

interface Props {
  visibleZoneTypes: Set<SpecialZoneType>;
  onToggleVisibleZoneType: (zoneType: SpecialZoneType) => void;
}

export function ZoneOverlayDropdown(props: Props) {
  const { visibleZoneTypes, onToggleVisibleZoneType } = props;
  const track = useAlarmGroupTracking();
  const toggleZoneType = (zoneType: SpecialZoneType) => {
    track('alarm_group_toggle_zone_overlay', { zoneType });
    onToggleVisibleZoneType(zoneType);
  };
  const isZoneOverlayEnabled = visibleZoneTypes.size > 0;
  // Any in-flight per-camera detail fetch counts as "loading zones". The
  // footer slot below is always rendered to keep the popup height stable;
  // its content only fades in while a fetch is in progress.
  const fetchingCameraCount = useIsFetching(['cameras']);
  const isLoadingZones = isZoneOverlayEnabled && fetchingCameraCount > 0;

  return (
    <Popover className="relative">
      <Popover.Button
        title="Zone overlays"
        className={clsx(
          'focus-visible:ring-primary-500 dark:ring-offset-dark-bg inline-flex h-9 w-9 items-center justify-center rounded-md border shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'border-onlight-line-2 dark:border-ondark-line-2 text-onlight-text-2 dark:bg-ondark-bg-2 dark:text-ondark-text-1 bg-white',
          'enabled:hover:bg-onlight-bg-3 dark:enabled:hover:bg-ondark-bg-3',
          isZoneOverlayEnabled &&
            'border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-200'
        )}
      >
        <LandPlotIcon className="h-4 w-4 stroke-[1.8]" />
      </Popover.Button>
      <Popover.Panel className="absolute right-0 top-11 z-20 w-56">
        <div className="border-onlight-line-2 dark:border-ondark-line-2 bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-xl border p-1.5 shadow-[0_12px_32px_rgba(15,23,42,0.14)]">
          {ZONE_OVERLAY_OPTIONS.map((option) => {
            const isSelected = visibleZoneTypes.has(option.id);

            return (
              <div
                key={option.id}
                role="button"
                tabIndex={0}
                onClick={() => toggleZoneType(option.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleZoneType(option.id);
                  }
                }}
                className="hover:bg-onlight-bg-3 dark:hover:bg-ondark-bg-3 flex min-h-[2.25rem] cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <LandPlotIcon
                    className={clsx('h-4 w-4 fill-current', option.iconColor)}
                  />
                  <span className="text-onlight-text-1 dark:text-ondark-text-1 text-sm">
                    {option.name}
                  </span>
                </div>
                <Checkbox
                  checked={isSelected}
                  onChange={() => toggleZoneType(option.id)}
                />
              </div>
            );
          })}
          <div
            aria-live="polite"
            className={clsx(
              'mt-1 flex h-6 items-center justify-center gap-1.5 px-3 text-xs transition-opacity duration-150',
              'text-onlight-text-2 dark:text-ondark-text-2',
              isLoadingZones ? 'opacity-100' : 'opacity-0'
            )}
          >
            <HakimoSpinner className="[&_svg]:h-3 [&_svg]:w-3" />
            <span>Loading zones…</span>
          </div>
        </div>
      </Popover.Panel>
    </Popover>
  );
}

export default ZoneOverlayDropdown;
